import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import cx from "@hsl/core/utils/cx";
import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { type Config, type TabConfig } from "../../config/types";
import * as TableComponent from "../Table";
import { useColumns } from "./hooks";

export interface FundDeps {
    priceType?: string;
}
interface Props {
    data: TopLevelDataObj[];
    tab: TabConfig;
    fundDeps?: FundDeps;
    config: Config;
    containerClassName?: string;
    footnote?: string;
    hidePreface?: boolean;
}

const Table = ({
    data,
    tab,
    fundDeps,
    config,
    containerClassName,
    footnote,
    hidePreface,
}: Props) => {
    const tabConfig = config.tabs.find((t) => t.key === tab.key);
    const confColumns =
        tab.columns?.length !== tabConfig?.columns?.length
            ? tab.columns
            : tabConfig?.columns;
    const confColumnsMobile =
        tab.columnsMobile?.length !== tabConfig?.columnsMobile?.length
            ? tab.columnsMobile
            : tabConfig?.columnsMobile;
    const columns = useColumns(
        tabConfig && {
            ...tabConfig,
            columns: confColumns,
            columnsMobile: confColumnsMobile,
        },
        fundDeps,
        data,
    );
    const table = useReactTable({
        data: data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className={cx("py-1", containerClassName)}>
            <TableComponent.Preface className="mb-4">
                {!hidePreface && tabConfig?.preface}
            </TableComponent.Preface>
            <TableComponent.Table className="text-sm">
                <TableComponent.Head>
                    {table.getHeaderGroups().map((headerGroup, i) => (
                        <TableComponent.TR key={`${i}-${headerGroup.id}`}>
                            {headerGroup.headers.map((header, i) => (
                                <TableComponent.TH
                                    key={`${i}-${header.id}`}
                                    colSpan={header.colSpan}
                                    style={header.column.columnDef.meta?.style}
                                    className={cx(
                                        header.column.columnDef?.meta
                                            ?.headerParentClassName,
                                        i === 0 && "pl-0",
                                    )}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext(),
                                          )}
                                </TableComponent.TH>
                            ))}
                        </TableComponent.TR>
                    ))}
                </TableComponent.Head>
                <TableComponent.Body>
                    {table.getRowModel().rows.map((row, i) => (
                        <TableComponent.TR key={`${i}-${row.id}`} withBorder>
                            {row.getVisibleCells().map((cell, i) => {
                                let context = cell.getContext();
                                const transformer =
                                    cell.column.columnDef?.meta
                                        ?.transformContext;
                                if (transformer) {
                                    context = transformer(context);
                                }
                                return (
                                    <TableComponent.TD
                                        key={`${i}-${cell.id}`}
                                        className={cx(
                                            cell.column.columnDef?.meta
                                                ?.cellParentClassName,
                                            i === 0 && "pl-0",
                                        )}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            context,
                                        )}
                                    </TableComponent.TD>
                                );
                            })}
                        </TableComponent.TR>
                    ))}
                </TableComponent.Body>
            </TableComponent.Table>
            <TableComponent.Footnote>{footnote}</TableComponent.Footnote>
        </div>
    );
};

export default Table;
